/**
 * Settings.jsx
 * The handful of things worth changing without a deploy.
 *
 * Anything not set here falls back to the value in the Worker's config, so an
 * empty settings table is a working app.
 */

import { useEffect, useState } from 'react';
import { api } from '../services/api.js';
import McpPanel from '../components/McpPanel.jsx';
import DeviceNotifications from '../components/DeviceNotifications.jsx';
import { useSession } from '../hooks/useSession.jsx';
import { Settings as SettingsIcon, Database, Warning, Check, Clock, Link } from '../components/Icons.jsx';

export default function Settings() {
  const { user } = useSession();
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // The alert services the server offers, and how the test went.
  const [services, setServices] = useState([]);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // What a link will come out looking like, refreshed as the fields change.
  const [linkExample, setLinkExample] = useState(null);

  useEffect(() => {
    api.settings
      .get()
      .then(({ settings, alertServices }) => {
        setServices(alertServices ?? []);
        setForm({
          capPounds: (settings.ai_monthly_cap_pence / 100).toFixed(2),
          defaultTime: settings.default_post_time,
          instagramReminder: settings.instagram_daily_reminder,
          alertUrl: settings.alerts_webhook_url ?? '',
          alertService: settings.alerts_service ?? 'ntfy',
          tagLinks: settings.link_tagging_enabled,
          utmMedium: settings.utm_medium ?? 'social',
          utmCampaign: settings.utm_campaign_template ?? '',
        });
      })
      .catch(setError);
  }, []);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.settings.update({
        ai_monthly_cap_pence: Math.round(Number(form.capPounds) * 100),
        default_post_time: form.defaultTime,
        instagram_daily_reminder: form.instagramReminder,
        alerts_webhook_url: form.alertUrl,
        alerts_service: form.alertService,
        link_tagging_enabled: form.tagLinks,
        utm_medium: form.utmMedium,
        utm_campaign_template: form.utmCampaign,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  // Shown rather than described: "{brand}-{date}" does not read as
  // "archevot-2026-09-10" until you have watched it become that.
  useEffect(() => {
    if (!form?.tagLinks) return undefined;
    let cancelled = false;

    api.settings
      .linkPreview({
        utm_medium: form.utmMedium,
        utm_campaign_template: form.utmCampaign,
      })
      .then((r) => {
        if (!cancelled) setLinkExample(r.url);
      })
      .catch(() => {
        if (!cancelled) setLinkExample(null);
      });

    return () => {
      cancelled = true;
    };
  }, [form?.tagLinks, form?.utmMedium, form?.utmCampaign]);

  /** Proves the address works now, rather than at 7am when a post fails. */
  async function sendTest() {
    setTesting(true);
    setTestResult(null);
    try {
      // Saved first: testing an address you have typed but not saved would
      // test the old one and tell you it worked.
      await api.settings.update({
        alerts_webhook_url: form.alertUrl,
        alerts_service: form.alertService,
      });
      const res = await api.settings.testAlert();
      setTestResult({ ok: true, message: res.message });
    } catch (e) {
      setTestResult({ ok: false, message: e.message });
    } finally {
      setTesting(false);
    }
  }

  if (error && !form) {
    return (
      <div className="notice notice--error">
        <Warning />
        <span>{error.message}</span>
      </div>
    );
  }

  if (!form) return <p className="mono">Loading…</p>;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Settings</div>
          <h1>Preferences</h1>
          <p>Small things that used to need a code change.</p>
        </div>
        <div className="page-head__actions">
          <button className="btn btn--primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {saved && (
        <div className="notice notice--info">
          <Check />
          <span>Saved.</span>
        </div>
      )}

      <div className="grid">
        <section className="card">
          <div className="card__head">
            <span className="tile">
              <Database size={21} />
            </span>
            <div className="card__head-text">
              <h2>AI spending</h2>
              <p>Caption generation stops automatically when the cap is reached.</p>
            </div>
          </div>

          <div className="field">
            <label htmlFor="cap">Monthly cap (£)</label>
            <input
              id="cap"
              type="text"
              inputMode="decimal"
              value={form.capPounds}
              onChange={(e) => setForm((f) => ({ ...f, capPounds: e.target.value }))}
            />
            <p className="hint">
              This is Kreeboom&apos;s own limit, from its own usage log. Keep a spend limit set in
              the OpenAI dashboard as the real backstop.
            </p>
          </div>
        </section>

        <section className="card">
          <div className="card__head">
            <span className="tile">
              <Clock size={21} />
            </span>
            <div className="card__head-text">
              <h2>Scheduling</h2>
              <p>Defaults for the Create screen. All times are London.</p>
            </div>
          </div>

          <div className="field">
            <label htmlFor="time">Default posting time</label>
            <input
              id="time"
              type="time"
              value={form.defaultTime}
              onChange={(e) => setForm((f) => ({ ...f, defaultTime: e.target.value }))}
            />
            <p className="hint">Used when you choose Schedule without picking a time.</p>
          </div>

          <div className="row">
            <div>
              <div className="row__label">Warn near Instagram&apos;s daily limit</div>
              <div className="row__sub">Instagram allows 25 posts per account per 24 hours</div>
            </div>
            <div className="row__end">
              <button
                className={`btn btn--sm ${form.instagramReminder ? 'btn--primary' : ''}`}
                onClick={() =>
                  setForm((f) => ({ ...f, instagramReminder: !f.instagramReminder }))
                }
              >
                {form.instagramReminder ? <Check size={15} /> : null}
                {form.instagramReminder ? 'On' : 'Off'}
              </button>
            </div>
          </div>
        </section>
      </div>

      <section className="card">
        <div className="card__head">
          <span className="tile">
            <Link size={21} />
          </span>
          <div className="card__head-text">
            <h2>Know which posts sent people to the site</h2>
            <p>
              Adds tracking parameters to links as they go out, so your own analytics can tell
              you a visitor came from Tuesday&apos;s Facebook post rather than just
              &ldquo;social&rdquo;. What you typed is kept exactly as you typed it — only the
              copy sent to Facebook and Instagram is tagged.
            </p>
          </div>
        </div>

        <div className="row">
          <div>
            <div className="row__label">Tag links when posting</div>
            <div className="row__sub">
              A link that already has a utm_source of its own is left alone
            </div>
          </div>
          <div className="row__end">
            <button
              className={`btn btn--sm ${form.tagLinks ? 'btn--primary' : ''}`}
              onClick={() => setForm((f) => ({ ...f, tagLinks: !f.tagLinks }))}
            >
              {form.tagLinks ? <Check size={15} /> : null}
              {form.tagLinks ? 'On' : 'Off'}
            </button>
          </div>
        </div>

        {form.tagLinks && (
          <>
            <div className="field">
              <label htmlFor="utm-medium">utm_medium</label>
              <input
                id="utm-medium"
                value={form.utmMedium}
                onChange={(e) => setForm((f) => ({ ...f, utmMedium: e.target.value }))}
              />
              <p className="hint">&ldquo;social&rdquo; unless you have a reason to say otherwise.</p>
            </div>

            <div className="field">
              <label htmlFor="utm-campaign">utm_campaign</label>
              <input
                id="utm-campaign"
                value={form.utmCampaign}
                onChange={(e) => setForm((f) => ({ ...f, utmCampaign: e.target.value }))}
                placeholder="{brand}-{date}"
              />
              <p className="hint">
                {'{brand}'}, {'{platform}'} and {'{date}'} are filled in when the post goes out.
                Leave it empty to send no campaign at all.
              </p>
            </div>

            {linkExample && (
              <div className="notice notice--info">
                <Check />
                <span style={{ wordBreak: 'break-all' }}>
                  A link would go out as {linkExample}
                </span>
              </div>
            )}

            <p className="hint">
              utm_source is always the platform — facebook or instagram — because that is
              the question this answers. Note that Instagram does not make links in captions
              clickable, so this mainly helps on Facebook and on your link-in-bio page.
            </p>
          </>
        )}
      </section>

      <DeviceNotifications />

      <section className="card">
        <div className="card__head">
          <span className="tile">
            <Warning size={21} />
          </span>
          <div className="card__head-text">
            <h2>Also send alerts elsewhere</h2>
            <p>
              Everything already goes to the bell at the top, and to this device if you turned
              that on above. To get alerts in ntfy, Slack, Discord or Telegram as well, add
              the address here. This is optional.
            </p>
          </div>
        </div>

        <div className="field">
          <label htmlFor="alert-service">What is at the other end</label>
          <select
            id="alert-service"
            value={form.alertService}
            onChange={(e) => setForm((f) => ({ ...f, alertService: e.target.value }))}
          >
            {services.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
          <p className="hint">
            {services.find((s) => s.key === form.alertService)?.hint ??
              'Pick where the message should go.'}
          </p>
        </div>

        <div className="field">
          <label htmlFor="alert-url">Address</label>
          <input
            id="alert-url"
            type="url"
            placeholder="https://ntfy.sh/kreeboom-something-only-you-know"
            value={form.alertUrl}
            onChange={(e) => setForm((f) => ({ ...f, alertUrl: e.target.value }))}
          />
          <p className="hint">
            Must start with https. Leave it empty and nothing is sent. You will hear about a post
            that has failed for good, and an account that needs reconnecting — each once, not
            every minute.
          </p>
        </div>

        <div className="btn-row">
          <button className="btn" onClick={sendTest} disabled={testing || !form.alertUrl}>
            {testing ? 'Sending…' : 'Send a test'}
          </button>
        </div>

        {testResult && (
          <div className={`notice notice--${testResult.ok ? 'info' : 'error'}`}>
            {testResult.ok ? <Check /> : <Warning />}
            <span>{testResult.message}</span>
          </div>
        )}
      </section>

      <section className="card">
        <div className="card__head">
          <span className="tile">
            <SettingsIcon size={21} />
          </span>
          <div className="card__head-text">
            <h2>This installation</h2>
            <p>Things that are fixed, and where to change them if you must.</p>
          </div>
        </div>

        <div className="rows">
          <Row label="Signed in as" value={user || '—'} sub="Managed by Cloudflare Access" />
          <Row label="Timezone" value="Europe/London" sub="Stored in UTC, shown in London time" />
          <Row
            label="App address"
            value="social.kreeboom.com"
            sub="Media served from media.kreeboom.com"
          />
          <Row
            label="Publishing checks"
            value="Every minute"
            sub="Plus daily maintenance at 03:15"
          />
        </div>
      </section>

      <McpPanel />
    </>
  );
}

function Row({ label, value, sub }) {
  return (
    <div className="row">
      <div>
        <div className="row__label">{label}</div>
        <div className="row__sub">{sub}</div>
      </div>
      <div className="row__end">{value}</div>
    </div>
  );
}
